import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea, Label } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertTriangle, CheckCircle2, AlertCircle } from "lucide-react";
import { formatNum } from "../lib/utils";
import { toast } from "sonner";

const COLUMNS = [
  { key: "open", label: "Open", icon: AlertTriangle, tone: "text-red-400 border-red-500/30" },
  { key: "escalated", label: "Escalated", icon: AlertCircle, tone: "text-amber-400 border-amber-500/30" },
  { key: "resolved", label: "Resolved", icon: CheckCircle2, tone: "text-emerald-400 border-emerald-500/30" },
];

export default function Anomalies() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState("");

  const load = async () => {
    const { data } = await api.get("/anomalies");
    setItems(data.items);
  };
  useEffect(() => { load(); }, []);

  const resolve = async (action) => {
    await api.post(`/anomalies/${selected.id}/resolve`, { resolution, action });
    toast.success(`Anomaly ${action === "escalate" ? "escalated" : "resolved"}`);
    setSelected(null);
    setResolution("");
    load();
  };

  const byStatus = COLUMNS.reduce((acc, c) => {
    acc[c.key] = items.filter((i) => i.status === c.key);
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="label-eyebrow mb-1">Module</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Anomaly Detection</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          4-layer engine: statistical outliers · contextual ESG · cross-metric consistency · regulatory compliance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="anomaly-kanban">
        {COLUMNS.map((col) => (
          <div key={col.key} className="card-flat" data-testid={`column-${col.key}`}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <col.icon className={`h-4 w-4 ${col.tone.split(' ')[0]}`} />
                <span className="font-display font-semibold">{col.label}</span>
              </div>
              <Badge variant="outline">{byStatus[col.key]?.length || 0}</Badge>
            </div>
            <div className="p-3 space-y-2 min-h-[200px]">
              {(byStatus[col.key] || []).map((a) => (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  className={`w-full text-left p-4 bg-surface border ${col.tone} rounded-sm hover:bg-surface-hover transition-colors`}
                  data-testid="anomaly-card"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={a.severity === "high" ? "danger" : "warning"} className="text-[10px]">{a.severity}</Badge>
                    <span className="text-[10px] font-mono text-muted-foreground">{a.period}</span>
                  </div>
                  <div className="font-medium text-sm">{a.metric}</div>
                  <div className="text-xs text-muted-foreground mt-1">{a.facility || "—"}</div>
                  <div className="mt-3 pt-3 border-t border-border/50 flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Deviation</div>
                      <div className={`font-mono font-bold ${a.deviation_pct > 0 ? "text-red-400" : "text-blue-400"}`}>
                        {a.deviation_pct > 0 ? "+" : ""}{a.deviation_pct}%
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Value</div>
                      <div className="font-mono text-sm">{formatNum(a.value)}</div>
                    </div>
                  </div>
                </button>
              ))}
              {!(byStatus[col.key] || []).length && (
                <div className="text-xs text-muted-foreground text-center py-8">No anomalies</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.metric} — {selected.period}</DialogTitle>
                <DialogDescription>{selected.description}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3 font-mono text-sm">
                <div className="card-flat p-3">
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Value</div>
                  <div className="text-lg font-bold">{formatNum(selected.value)}</div>
                </div>
                <div className="card-flat p-3">
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Baseline</div>
                  <div className="text-lg font-bold">{formatNum(selected.baseline)}</div>
                </div>
                <div className="card-flat p-3">
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Deviation</div>
                  <div className="text-lg font-bold text-red-400">{selected.deviation_pct}%</div>
                </div>
              </div>
              {selected.status === "open" && (
                <>
                  <div>
                    <Label>Resolution notes</Label>
                    <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Describe investigation findings, root cause, evidence…" data-testid="resolution-notes" />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => resolve("escalate")} className="flex-1" disabled={!resolution} data-testid="escalate-btn">Escalate</Button>
                    <Button variant="primary" onClick={() => resolve("confirm")} className="flex-1" disabled={!resolution} data-testid="confirm-btn">Confirm & Resolve</Button>
                  </div>
                </>
              )}
              {selected.status !== "open" && selected.resolution && (
                <div className="card-flat p-3 bg-surface">
                  <div className="label-eyebrow mb-1">Resolution</div>
                  <div className="text-sm">{selected.resolution}</div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
